import type { FeedbackContext } from '@/components/feedback/feedback-button';

export interface FeedbackContextInfo {
  context: FeedbackContext;
  sessionId?: string;
  participantId?: string;
}

/**
 * Context detection service for feedback submission
 * Implements requirements 2.4, 2.5, 6.2, 6.3
 */
export class FeedbackContextService {
  /**
   * Detect current feedback context based on URL path
   */
  static detectContext(): FeedbackContext {
    const path = window.location.pathname;
    
    if (path.includes('/demographics')) {
      return 'demographics';
    } else if (path.includes('/annotation')) {
      return 'annotation';
    } else if (path.includes('/complete')) {
      return 'completion';
    }
    
    // Default fallback
    return 'annotation';
  }

  /**
   * Get current session ID from various sources
   */
  static getCurrentSessionId(): string | undefined {
    // Try to get from URL params first
    const urlParams = new URLSearchParams(window.location.search);
    const sessionFromUrl = urlParams.get('sessionId');
    if (sessionFromUrl) {
      return sessionFromUrl;
    }

    // Try to get from sessionStorage (set by annotation workflow)
    const sessionFromStorage = sessionStorage.getItem('currentSessionId');
    if (sessionFromStorage) {
      return sessionFromStorage;
    }

    // Try to get from localStorage as fallback
    const sessionFromLocal = localStorage.getItem('lastSessionId');
    if (sessionFromLocal) {
      return sessionFromLocal;
    }

    return undefined;
  }

  /**
   * Get current participant ID from various sources
   */
  static getCurrentParticipantId(): string | undefined {
    // Try to get from sessionStorage (set by demographics workflow)
    const participantFromStorage = sessionStorage.getItem('currentParticipantId');
    if (participantFromStorage) {
      return participantFromStorage;
    }

    // Try to get from localStorage as fallback
    const participantFromLocal = localStorage.getItem('participantId');
    if (participantFromLocal) {
      return participantFromLocal;
    }

    return undefined;
  }

  /**
   * Get complete feedback context information
   */
  static getFeedbackContextInfo(): FeedbackContextInfo {
    return {
      context: this.detectContext(),
      sessionId: this.getCurrentSessionId(),
      participantId: this.getCurrentParticipantId()
    };
  }

  /**
   * Store session ID for context detection
   */
  static setCurrentSessionId(sessionId: string): void {
    sessionStorage.setItem('currentSessionId', sessionId);
    localStorage.setItem('lastSessionId', sessionId);
  }

  /**
   * Store participant ID for context detection
   */
  static setCurrentParticipantId(participantId: string): void {
    sessionStorage.setItem('currentParticipantId', participantId);
    localStorage.setItem('participantId', participantId);
  }

  /**
   * Clear context information (e.g., on logout)
   */
  static clearContext(): void {
    sessionStorage.removeItem('currentSessionId');
    sessionStorage.removeItem('currentParticipantId');
    // Keep localStorage for potential future sessions
  }
}